import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConfigurationTab } from "@presentation/hooks/useConfigurationTab";
import { Button } from "@progress/kendo-react-buttons";
import { Card, TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { usePermissionStore } from "@presentation/stores/PermissionStore";
import { useTeamPermission } from "@presentation/components/helpers/usePermissionCheck";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { RenameTeamDialog } from "@presentation/components/team/RenameTeamDialog";
import { AssignTeamRoleDialog } from "@presentation/components/team/AssignTeamRoleDialog";
import { RemoveTeamMemberDialog } from "@presentation/components/team/RemoveTeamMemberDialog";
import { AcceptProjectMoveDialog } from "@presentation/components/team/AcceptProjectMoveDialog";
import { DeleteTeamDialog } from "@presentation/components/team/DeleteTeamDialog";
import { StorageConfigurationSection } from "@presentation/components/storageConfiguration/StorageConfigurationSection";
import { SoftwareVersionConfigurationSection } from "@presentation/components/softwareVersions/SoftwareVersionConfigurationSection";
import { GatedActionButton } from "@presentation/components/permission/GatedActionButton";
import { EntitlementLockedBadge } from "@presentation/components/permission/EntitlementLockedBadge";
import { gridIcon, folderIcon, pencilIcon, trashIcon } from "@progress/kendo-svg-icons";
import { fillCardStyle, mutedTextStyle, pageFillContentStyle, pageHeaderRegionStyle, pageHeaderStyle, pageShellStyle, pageTitleStyle, rowStyle } from "@presentation/common/pageLayout";
import { TeamActions, TeamRoles } from "@domain/schemas/workspace";
import type { TeamDetail, TeamMembership, TeamRoleName, ProjectMoveRequest } from "@domain/schemas/workspace";
import { useIsEntitled } from "@presentation/hooks/useEntitlements";
import { EntitlementReadOnlyNotice } from "@presentation/components/permission/EntitlementReadOnlyNotice";
import { CONTEXT_MANAGEMENT_ENTITLEMENT_NAME } from "@domain/valueObjects/contextManagementEntitlement";

type AssignRoleTarget = { mode: "add" } | { mode: "change"; member: TeamMembership };

type DialogState =
  | { kind: "none" }
  | { kind: "renameTeam" }
  | { kind: "deleteTeam" }
  | { kind: "assignRole"; target: AssignRoleTarget }
  | { kind: "removeMember"; userId: string }
  | { kind: "acceptMove"; requestId: string };

// Highest-privilege role first, matching RoleActionMatrix's permission ordering (Manager > ProjectLead > Member).
// Exported so a test can assert every `TeamRoleName` (see `TEAM_ROLE_NAMES`) has a rank here.
export const TEAM_ROLE_RANK: Record<TeamRoleName, number> = { [TeamRoles.Manager]: 0, [TeamRoles.ProjectLead]: 1, [TeamRoles.Member]: 2 };

/**
Team settings (`/settings?scope=team&teamId=...`) - name, members, pending incoming moves, storage and
software-version configuration. Project inventory lives on the sibling `/projects?teamId=` page, the
team's jobs on the sibling `/teams/:teamId/jobs` page.
*/
const TeamDetailPage: React.FC<{ teamId?: string }> = ({ teamId: selectedTeamId }) => {
  const { teamId: routeTeamId = "" } = useParams<{ teamId: string }>();
  const teamId = selectedTeamId ?? routeTeamId;
  const navigate = useNavigate();
  const teamManagementService = useInjection(TeamManagementService);
  const setActiveTeamContext = useWorkspaceContextStore((state) => state.setActiveTeamContext);
  const recoverFromStaleTeam = useWorkspaceContextStore((state) => state.recoverFromStaleTeam);
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const contextManagementEntitled = useIsEntitled(CONTEXT_MANAGEMENT_ENTITLEMENT_NAME);

  const [teamDetail, setTeamDetail] = useState<TeamDetail | null>(null);
  const [teamDetailError, setTeamDetailError] = useState<string | null>(null);
  const [pendingMoveRequests, setPendingMoveRequests] = useState<ProjectMoveRequest[]>([]);
  const [moveRequestError, setMoveRequestError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });
  const isReadOnlyDueToEntitlement = teamDetail?.isDefault === false && contextManagementEntitled === false;

  const userIdsToResolve = React.useMemo(
    () => [...(teamDetail?.memberships.map((membership) => membership.userId) ?? []), ...pendingMoveRequests.map((request) => request.proposedByUserId)],
    [teamDetail, pendingMoveRequests],
  );
  const userNames = useUserDisplayNames(userIdsToResolve);

  const sortedMemberships = React.useMemo(() => {
    const memberships = teamDetail?.memberships ?? [];
    return memberships.toSorted((left, right) => {
      const rankDifference = TEAM_ROLE_RANK[left.role] - TEAM_ROLE_RANK[right.role];
      return rankDifference === 0 ? formatUserDisplayName(userNames[left.userId], left.userId).localeCompare(formatUserDisplayName(userNames[right.userId], right.userId)) : rankDifference;
    });
  }, [teamDetail, userNames]);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const filteredMemberships = React.useMemo(() => {
    const normalizedTerm = memberSearchTerm.trim().toLowerCase();
    return normalizedTerm ? sortedMemberships.filter((membership) => formatUserDisplayName(userNames[membership.userId], membership.userId).toLowerCase().includes(normalizedTerm)) : sortedMemberships;
  }, [sortedMemberships, memberSearchTerm, userNames]);

  // ViewInventory is never entitlement-gated (reads are never gated server-side), so it needs no isDefault.
  const canViewInventory = useTeamPermission(teamId, TeamActions.ViewInventory).allowed;
  // Every other team action can be an entitlement gap on a confirmed non-default team (WP8).
  const renameTeamPermission = useTeamPermission(teamId, TeamActions.RenameTeam, teamDetail?.isDefault);
  const manageMembershipPermission = useTeamPermission(teamId, TeamActions.ManageMembership, teamDetail?.isDefault);
  const assignTeamRolesPermission = useTeamPermission(teamId, TeamActions.AssignTeamRoles, teamDetail?.isDefault);
  const canManageMembership = manageMembershipPermission.allowed;
  const storageConfigurationPermission = useTeamPermission(teamId, TeamActions.ManageStorageConfiguration, teamDetail?.isDefault);
  const softwareVersionsPermission = useTeamPermission(teamId, TeamActions.ManageSoftwareVersions, teamDetail?.isDefault);
  const canManageStorageConfiguration = storageConfigurationPermission.allowed;
  const canManageSoftwareVersions = softwareVersionsPermission.allowed;
  const showsMoveRequests = (canManageMembership || manageMembershipPermission.reason === "entitlement") && pendingMoveRequests.length > 0;
  const [selectedTab, setSelectedTab] = useConfigurationTab(["members", ...(showsMoveRequests ? ["move-requests"] : []), "storage", "software-versions"]);
  useEffect(() => {
    if (teamId) setActiveTeamContext(teamId);
  }, [teamId, setActiveTeamContext]);

  const reloadTeamDetail = useCallback(async () => {
    try {
      const detail = await teamManagementService.getTeamAsync(teamId);
      setTeamDetail(detail);
      setTeamDetailError(null);
    } catch (error) {
      const { status } = extractProblemDetails(error);
      // A denied or not-found response means the team was deleted or access was revoked (WP6.5).
      if (status === 403 || status === 404) {
        recoverFromStaleTeam(teamId);
        navigate("/teams");
        return;
      }
      setTeamDetail(null);
      setTeamDetailError(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not load this team.");
    }
  }, [teamId, teamManagementService, recoverFromStaleTeam, navigate]);

  const reloadPendingMoveRequests = useCallback(async () => {
    try {
      setPendingMoveRequests(await teamManagementService.listPendingProjectMoveRequestsAsync(teamId));
    } catch {
      setPendingMoveRequests([]);
    }
  }, [teamId, teamManagementService]);

  useEffect(() => {
    if (!teamId || !canViewInventory) {
      setTeamDetail(null);
      return;
    }
    void reloadTeamDetail();
  }, [teamId, canViewInventory, reloadTeamDetail]);

  useEffect(() => {
    if (!teamId || !canManageMembership) {
      setPendingMoveRequests([]);
      return;
    }
    void reloadPendingMoveRequests();
  }, [teamId, canManageMembership, reloadPendingMoveRequests]);

  const handleResolveMoveRequest = async (requestId: string, resolution: "accept" | "reject") => {
    setMoveRequestError(null);
    try {
      if (resolution === "reject") await teamManagementService.rejectProjectMoveAsync(teamId, requestId);
      void reloadPendingMoveRequests();
    } catch (error) {
      setMoveRequestError(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not resolve this move request.");
    }
  };

  const handleDeleteTeam = () => {
    navigate("/teams");
  };

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        {teamDetailError && <p style={{ color: "#7f1d1d" }}>{teamDetailError}</p>}
        {teamDetail && (
          <div style={pageHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={pageTitleStyle}>Team settings{teamDetail ? ` - ${teamDetail.name}` : ""}</h1>
              {isReadOnlyDueToEntitlement && <EntitlementLockedBadge />}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button type="button" fillMode="flat" svgIcon={gridIcon} title="View jobs" aria-label="View jobs" onClick={() => navigate(`/jobs?teamId=${teamId}`)} />
              <Button type="button" fillMode="flat" svgIcon={folderIcon} title="View projects" aria-label="View projects" onClick={() => navigate(`/projects?teamId=${teamId}`)} />
              <GatedActionButton
                type="button"
                fillMode="flat"
                svgIcon={pencilIcon}
                title="Rename team"
                aria-label="Rename team"
                permission={renameTeamPermission}
                onClick={() => setDialog({ kind: "renameTeam" })}
              />
              {isAdmin && (
                <GatedActionButton
                  type="button"
                  themeColor="error"
                  fillMode="flat"
                  svgIcon={trashIcon}
                  title="Delete team"
                  aria-label="Delete team"
                  permission={{ allowed: true, reason: null }}
                  entitlementActive={contextManagementEntitled}
                  onClick={() => setDialog({ kind: "deleteTeam" })}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {isReadOnlyDueToEntitlement && <EntitlementReadOnlyNotice />}

      <div style={pageFillContentStyle}>
        {teamDetail && (
          <Card style={fillCardStyle}>
            <TabStrip className="scrollable-tabstrip" selected={selectedTab} onSelect={(event) => setSelectedTab(event.selected)}>
              <TabStripTab title="Members">
                <div style={{ display: "grid", gap: 10, padding: "4px 0 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 1rem" }}>
                    <input
                      type="search"
                      className="project-sidebar-search"
                      placeholder="Search members"
                      value={memberSearchTerm}
                      onChange={(event) => setMemberSearchTerm(event.target.value)}
                      style={{ minWidth: 220 }}
                    />
                    <GatedActionButton type="button" permission={assignTeamRolesPermission} onClick={() => setDialog({ kind: "assignRole", target: { mode: "add" } })}>
                      Add member
                    </GatedActionButton>
                  </div>
                  <Grid data={filteredMemberships} dataItemKey="id">
                    <GridColumn
                      field="userId"
                      title="Name"
                      cell={(properties: GridCustomCellProps) => <td>{formatUserDisplayName(userNames[properties.dataItem.userId], properties.dataItem.userId)}</td>}
                    />
                    <GridColumn field="role" title="Role" width="160px" />
                    <GridColumn
                      title=""
                      width="90px"
                      cell={(properties: GridCustomCellProps) => (
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <GatedActionButton
                              type="button"
                              fillMode="flat"
                              svgIcon={pencilIcon}
                              title="Change role"
                              aria-label="Change role"
                              permission={assignTeamRolesPermission}
                              onClick={() => setDialog({ kind: "assignRole", target: { mode: "change", member: properties.dataItem } })}
                            />
                            <GatedActionButton
                              type="button"
                              fillMode="flat"
                              themeColor="error"
                              svgIcon={trashIcon}
                              title="Remove"
                              aria-label="Remove"
                              permission={manageMembershipPermission}
                              onClick={() => setDialog({ kind: "removeMember", userId: properties.dataItem.userId })}
                            />
                          </div>
                        </td>
                      )}
                    />
                  </Grid>
                </div>
              </TabStripTab>

              {showsMoveRequests && (
                <TabStripTab title={`Project move requests (${pendingMoveRequests.length})`}>
                  <div style={{ display: "grid", gap: 10, padding: "4px 4px 12px" }}>
                    {moveRequestError && <p style={{ color: "#7f1d1d" }}>{moveRequestError}</p>}
                    {pendingMoveRequests.map((request) => (
                      <div key={request.id} style={rowStyle}>
                        {/* No project name available here - the moving-in project isn't yet in this team's inventory. */}
                        <span>
                          Project <strong>{request.projectId}</strong> proposed by {formatUserDisplayName(userNames[request.proposedByUserId], request.proposedByUserId)}
                        </span>
                        <div style={{ display: "flex", gap: 8 }}>
                          <GatedActionButton type="button" fillMode="flat" permission={manageMembershipPermission} onClick={() => setDialog({ kind: "acceptMove", requestId: request.id })}>
                            Accept
                          </GatedActionButton>
                          <GatedActionButton type="button" fillMode="flat" themeColor="error" permission={manageMembershipPermission} onClick={() => handleResolveMoveRequest(request.id, "reject")}>
                            Reject
                          </GatedActionButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabStripTab>
              )}

              <TabStripTab title="Storage configuration">
                <div style={{ padding: "4px 4px 12px" }}>
                  <StorageConfigurationSection level="team" contextId={teamId} canManage={canManageStorageConfiguration} />
                </div>
              </TabStripTab>

              <TabStripTab title="Software versions">
                <div style={{ display: "grid", gap: 12, padding: "4px 4px 12px" }}>
                  <p style={mutedTextStyle}>
                    {canManageSoftwareVersions
                      ? "Allowed and default software versions for this team. Clearing all overrides falls back to the deployment's defaults."
                      : "Resolved from the deployment and team software-version configuration."}
                  </p>
                  <SoftwareVersionConfigurationSection level="team" contextId={teamId} canManage={canManageSoftwareVersions} />
                </div>
              </TabStripTab>
            </TabStrip>
          </Card>
        )}
      </div>

      {dialog.kind === "renameTeam" && teamDetail && <RenameTeamDialog teamId={teamId} currentName={teamDetail.name} onClose={() => setDialog({ kind: "none" })} onSuccess={setTeamDetail} />}
      {dialog.kind === "deleteTeam" && teamDetail && <DeleteTeamDialog teamId={teamId} teamName={teamDetail.name} onClose={() => setDialog({ kind: "none" })} onSuccess={handleDeleteTeam} />}
      {dialog.kind === "assignRole" && (
        <AssignTeamRoleDialog
          teamId={teamId}
          existingMember={dialog.target.mode === "change" ? { userId: dialog.target.member.userId, role: dialog.target.member.role as TeamRoleName } : undefined}
          existingMemberUserIds={teamDetail?.memberships.map((membership) => membership.userId)}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={setTeamDetail}
        />
      )}
      {dialog.kind === "removeMember" && <RemoveTeamMemberDialog teamId={teamId} userId={dialog.userId} onClose={() => setDialog({ kind: "none" })} onSuccess={setTeamDetail} />}
      {dialog.kind === "acceptMove" && (
        <AcceptProjectMoveDialog
          teamId={teamId}
          requestId={dialog.requestId}
          teamMemberships={teamDetail?.memberships ?? []}
          userNames={userNames}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={() => void reloadPendingMoveRequests()}
        />
      )}
    </div>
  );
};

export default TeamDetailPage;
